import { nativeBridge } from "../index"
import type {
  HealthConnectParams,
  HealthConnectResponse,
  HealthConnectionStatusResponse,
  HealthAutoSyncStatusResponse,
  HealthToggleAutoSyncParams,
  HealthGetWorkoutsParams,
  HealthGetWorkoutsResponse,
  HealthExportWorkoutParams,
  HealthExportWorkoutResponse,
  HealthMarkWorkoutImportedParams,
} from "../types"
import { addLogError, addLogInfo } from "../../logger"
import config from "@/config"

/**
 * Apple Health API
 * Provides methods for interacting with Apple Health through the native bridge
 */
export class HealthAPI {
  /**
   * Initiate connection to Apple Health
   * @param timeout Optional timeout in milliseconds
   * @returns Connection status and optional message
   */
  async connect(timeout?: number): Promise<HealthConnectResponse> {
    return nativeBridge.request<HealthConnectParams, HealthConnectResponse>("health.connect", {}, timeout || 30000)
  }

  /**
   * Check Apple Health connection status
   * @returns Current connection status
   */
  async getConnectionStatus(): Promise<HealthConnectionStatusResponse> {
    return nativeBridge.request<void, HealthConnectionStatusResponse>("health.getConnectionStatus", undefined, 5000)
  }

  /**
   * Get auto-sync status
   * @returns Current auto-sync enabled state
   */
  async getAutoSyncStatus(): Promise<HealthAutoSyncStatusResponse> {
    return nativeBridge.request<void, HealthAutoSyncStatusResponse>("health.getAutoSyncStatus", undefined, 5000)
  }

  /**
   * Toggle auto-sync setting
   * @param enabled Whether to enable or disable auto-sync
   */
  async toggleAutoSync(enabled: boolean): Promise<void> {
    await nativeBridge.request<HealthToggleAutoSyncParams, void>("health.toggleAutoSync", { enabled }, 5000)
  }

  /**
   * Get workouts from Apple Health
   * @param days Number of days to fetch (default: 30)
   * @returns List of workouts and total count
   */
  async getWorkouts(days = 60): Promise<HealthGetWorkoutsResponse> {
    return nativeBridge.request<HealthGetWorkoutsParams, HealthGetWorkoutsResponse>(
      "health.getWorkouts",
      { days },
      10000,
    )
  }

  /**
   * Export workout to FIT format
   *
   * Reading a long workout out of HealthKit and encoding it takes a while on
   * an older phone, so the wait is generous — this is the only way the web
   * side gets the file.
   *
   * @param workoutId The workout ID to export
   * @returns Base64 encoded FIT file data and filename
   */
  async exportWorkoutToFit(workoutId: string): Promise<HealthExportWorkoutResponse> {
    return nativeBridge.request<HealthExportWorkoutParams, HealthExportWorkoutResponse>(
      "health.exportWorkout",
      { workoutId },
      60000,
    )
  }

  /**
   * Tell native a workout is already uploaded
   *
   * Auto-sync keeps its own list of uploaded workouts; recording the web
   * import there stops the two from uploading the same workout twice. Builds
   * that predate the method reject with METHOD_NOT_FOUND — callers treat that
   * as a soft failure.
   *
   * @param workoutId The workout ID that reached the server
   */
  async markWorkoutImported(workoutId: string): Promise<void> {
    await nativeBridge.request<HealthMarkWorkoutImportedParams, void>("health.markWorkoutImported", { workoutId }, 5000)
  }

  /**
   * Get workout as file data for upload
   * @param workoutId The workout ID to get as file
   * @returns ArrayBuffer containing workout file data
   */
  async getWorkoutFile(workoutId: string): Promise<ArrayBuffer> {
    const response = await this.exportWorkoutToFit(workoutId)
    const blob = this.base64ToBlob(response.data)
    return blob.arrayBuffer()
  }

  /**
   * Convert base64 FIT data to Blob for download
   * @param base64Data Base64 encoded FIT file data
   * @returns Blob object
   */
  base64ToBlob(base64Data: string): Blob {
    const byteCharacters = atob(base64Data)
    const byteNumbers = new Array(byteCharacters.length)

    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i)
    }

    const byteArray = new Uint8Array(byteNumbers)
    return new Blob([byteArray], { type: "application/octet-stream" })
  }
}

/** Interface for Health API operations, implemented by both real and mock APIs */
export interface IHealthAPI {
  connect(timeout?: number): Promise<HealthConnectResponse>
  getConnectionStatus(): Promise<HealthConnectionStatusResponse>
  getAutoSyncStatus(): Promise<HealthAutoSyncStatusResponse>
  toggleAutoSync(enabled: boolean): Promise<void>
  getWorkouts(days?: number): Promise<HealthGetWorkoutsResponse>
  exportWorkoutToFit(workoutId: string): Promise<HealthExportWorkoutResponse>
  markWorkoutImported(workoutId: string): Promise<void>
  getWorkoutFile(workoutId: string): Promise<ArrayBuffer>
  base64ToBlob(base64Data: string): Blob
}

// Create the singleton instance
const realHealthAPI = new HealthAPI()

// The implementation in use: the real bridge, or the mock once it has loaded
let activeHealthAPI: IHealthAPI = realHealthAPI

// Only use mock if explicitly requested
if (typeof window !== "undefined" && (config.USE_MOCK_NATIVE_BRIDGE || window.location.search.includes("mock=true"))) {
  // Dynamically import mock for development
  import("./health.mock")
    .then(({ mockHealthAPI }) => {
      activeHealthAPI = mockHealthAPI
      addLogInfo("HealthAPI", "Using mock implementation (explicitly enabled)")
    })
    .catch((error) => addLogError("HealthAPI: failed to load mock implementation", error))
}

/**
 * The API the app talks to. A stable object that forwards every call to the
 * active implementation, so the mock can replace the real bridge after the
 * module has been imported without a mutable export.
 */
export const healthAPI: IHealthAPI = {
  connect: (timeout) => activeHealthAPI.connect(timeout),
  getConnectionStatus: () => activeHealthAPI.getConnectionStatus(),
  getAutoSyncStatus: () => activeHealthAPI.getAutoSyncStatus(),
  toggleAutoSync: (enabled) => activeHealthAPI.toggleAutoSync(enabled),
  getWorkouts: (days) => activeHealthAPI.getWorkouts(days),
  exportWorkoutToFit: (workoutId) => activeHealthAPI.exportWorkoutToFit(workoutId),
  markWorkoutImported: (workoutId) => activeHealthAPI.markWorkoutImported(workoutId),
  getWorkoutFile: (workoutId) => activeHealthAPI.getWorkoutFile(workoutId),
  base64ToBlob: (base64Data) => activeHealthAPI.base64ToBlob(base64Data),
}
